// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — dynamic-block authoring descriptors (D5)
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "dynamic" — split out of `dynamic.ts` for file-size hygiene
// (CLAUDE.md "Контроль размера файлов": dynamic.ts is already ~830 lines).
// Covers the block-authoring commands that were dispatched via literal
// `upperStart === "..."` comparisons in App.tsx's `submitCommandText`
// before the D5 router→registry unification landed:
//
//   • TESTBLOCK / CLOSETESTBLOCK — enter / exit the BEDIT "Test Block"
//     sandbox space. Dispatches `space/enter-block-test` /
//     `space/exit-block-test` (apps/web/src/app/state/blockEditReducer.ts).
//     Both literals were previously COVERAGE_ALLOWLISTed as "no CLI
//     counterpart" — that allowlist note predates the App.tsx CLI wiring
//     and is now stale; this descriptor pair is the real owner and the
//     literals are removed from the allowlist in the same change.
//   • BAUTHORPALETTECLOSE — closes the Block Authoring Palette inside a
//     BEDIT session (client-side toggle only, no EditorCommand literal).
//   • BVHIDE / BVSHOW / BVSHOWALL — hide / show the current selection in
//     the block's active (or every) visibility state. Dispatch
//     `geometry/bvhide` / `geometry/bvshow` / `geometry/bvshow-all`
//     (apps/web/src/app/state/geometryReducer.ts). Also removed from
//     COVERAGE_ALLOWLIST in this change.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_BLOCK_EDIT = "apps/web/src/app/state/blockEditReducer.ts";
const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts";

const TESTBLOCK: CommandDescriptor = {
  id: "TESTBLOCK",
  category: "dynamic",
  status: "implemented",
  // Session-lifecycle / editor-UI command (enters a preview sandbox) —
  // outside the AI drafting safe contour.
  aiCallable: false,
  aliases: { en: ["TESTBLOCK", "BTESTBLOCK"], ru: ["ТЕСТБЛОК"] },
  commandTypeLiterals: ["space/enter-block-test"],
  parameters: [],
  help: {
    ru: {
      short: "Открывает песочницу «Тест блока» внутри сессии BEDIT.",
      long: "Команда ТЕСТБЛОК (TESTBLOCK) доступна только внутри активной сессии BEDIT (Редактор блоков). Она создаёт временное пространство block-test, где можно опробовать поведение динамических параметров/действий блока (перетаскивание ручек, переключение состояний видимости) без применения изменений к исходному определению блока. Вне BEDIT команда — no-op с подсказкой.",
      examples: ["TESTBLOCK", "ТЕСТБЛОК"],
      seeAlso: ["BEDIT", "CLOSETESTBLOCK", "BVSTATE"],
    },
    en: {
      short: "Opens the \"Test Block\" sandbox inside a BEDIT session.",
      long: "TESTBLOCK is only available inside an active BEDIT (Block Editor) session. It creates a transient block-test space where dynamic parameter/action behaviour (grip dragging, visibility-state switching) can be tried without committing changes to the master block definition. Outside BEDIT the command is a no-op with a hint toast.",
      examples: ["TESTBLOCK", "BTESTBLOCK"],
      seeAlso: ["BEDIT", "CLOSETESTBLOCK", "BVSTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.testblock.label",
    labelKeyEn: "command.testblock.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "space/enter-block-test", file: REDUCER_BLOCK_EDIT },
  ],
};

const CLOSETESTBLOCK: CommandDescriptor = {
  id: "CLOSETESTBLOCK",
  category: "dynamic",
  status: "implemented",
  aiCallable: false,
  aliases: {
    en: ["CLOSETESTBLOCK", "BTESTBLOCKCLOSE"],
    ru: ["ЗАКРЫТЬТЕСТ", "ВЫЙТИТЕСТ"],
  },
  commandTypeLiterals: ["space/exit-block-test"],
  parameters: [],
  help: {
    ru: {
      short: "Закрывает песочницу «Тест блока» и возвращает в BEDIT.",
      long: "Команда ЗАКРЫТЬТЕСТ (CLOSETESTBLOCK) действует только внутри активного пространства block-test (открытого командой TESTBLOCK) и возвращает активное пространство обратно в block-edit. Вне block-test — no-op с подсказкой.",
      examples: ["CLOSETESTBLOCK", "ЗАКРЫТЬТЕСТ", "ВЫЙТИТЕСТ"],
      seeAlso: ["TESTBLOCK", "BEDIT"],
    },
    en: {
      short: "Closes the \"Test Block\" sandbox and returns to BEDIT.",
      long: "CLOSETESTBLOCK only acts inside an active block-test space (opened via TESTBLOCK) and returns the active space to block-edit. Outside block-test it is a no-op with a hint toast.",
      examples: ["CLOSETESTBLOCK", "BTESTBLOCKCLOSE"],
      seeAlso: ["TESTBLOCK", "BEDIT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.closetestblock.label",
    labelKeyEn: "command.closetestblock.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "space/exit-block-test", file: REDUCER_BLOCK_EDIT },
  ],
};

const BAUTHORPALETTECLOSE: CommandDescriptor = {
  id: "BAUTHORPALETTECLOSE",
  category: "dynamic",
  status: "implemented",
  aiCallable: false,
  aliases: {
    en: ["BAUTHORPALETTECLOSE"],
    ru: ["БПАЛИТРАЗАКР", "ЗАКРПАЛИТРА"],
  },
  // Local UI-visibility toggle only (blockAuthoringShell.palette.close()) —
  // no EditorCommand dispatch, mirrors BAUTHORPALETTE's own toggle.
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short: "Закрывает Палитру авторинга блоков внутри сессии BEDIT.",
      long: "Команда БПАЛИТРАЗАКР (BAUTHORPALETTECLOSE) скрывает панель авторинга блока (параметры / действия / состояния видимости) внутри активной сессии BEDIT. Это чисто визуальный переключатель — палитра не размонтируется, а прячется; повторное открытие через BAUTHORPALETTE восстанавливает её состояние.",
      examples: ["BAUTHORPALETTECLOSE", "БПАЛИТРАЗАКР"],
      seeAlso: ["BAUTHORPALETTE", "BPARAMETER", "BACTION"],
    },
    en: {
      short: "Closes the Block Authoring Palette inside a BEDIT session.",
      long: "BAUTHORPALETTECLOSE hides the block-authoring panel (parameters / actions / visibility states) inside an active BEDIT session. This is a purely visual toggle — the palette is not unmounted, only hidden; reopening via BAUTHORPALETTE restores its state.",
      examples: ["BAUTHORPALETTECLOSE"],
      seeAlso: ["BAUTHORPALETTE", "BPARAMETER", "BACTION"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bauthorpaletteclose.label",
    labelKeyEn: "command.bauthorpaletteclose.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const BVHIDE: CommandDescriptor = {
  id: "BVHIDE",
  category: "dynamic",
  status: "implemented",
  // Selection-scoped visibility-state edit inside BEDIT — outside the AI
  // drafting safe contour (no ТЗ §10.1 matrix entry).
  aiCallable: false,
  aliases: { en: ["BVHIDE"], ru: [] },
  commandTypeLiterals: ["geometry/bvhide"],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Выбранные сущности внутри block-edit, скрываемые в текущем состоянии видимости.",
        en: "Currently-selected entities inside block-edit to hide in the current visibility state.",
      },
    },
  ],
  help: {
    ru: {
      short: "Скрывает выделенные сущности в текущем состоянии видимости.",
      long: "Команда BVHIDE доступна только внутри BEDIT для блока с параметром видимости (visibility parameter). Она отмечает выбранные сущности как скрытые в ТЕКУЩЕМ состоянии видимости (currentStateId параметра). Требует непустого выделения; вне BEDIT или без параметра видимости — no-op с подсказкой.",
      examples: ["BVHIDE"],
      seeAlso: ["BVSHOW", "BVSHOWALL", "BVSTATE"],
    },
    en: {
      short: "Hides the selected entities in the current visibility state.",
      long: "BVHIDE is only available inside BEDIT for a block that has a visibility parameter. It marks the selected entities as hidden in the parameter's CURRENT state (currentStateId). Requires a non-empty selection; outside BEDIT or without a visibility parameter it is a no-op with a hint toast.",
      examples: ["BVHIDE"],
      seeAlso: ["BVSHOW", "BVSHOWALL", "BVSTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bvhide.label",
    labelKeyEn: "command.bvhide.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/bvhide", file: REDUCER_GEOMETRY },
  ],
};

const BVSHOW: CommandDescriptor = {
  id: "BVSHOW",
  category: "dynamic",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["BVSHOW"], ru: [] },
  commandTypeLiterals: ["geometry/bvshow"],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Выбранные сущности внутри block-edit, показываемые в текущем состоянии видимости.",
        en: "Currently-selected entities inside block-edit to show in the current visibility state.",
      },
    },
  ],
  help: {
    ru: {
      short: "Показывает выделенные сущности в текущем состоянии видимости.",
      long: "Команда BVSHOW — обратная BVHIDE: отмечает выбранные сущности как видимые в ТЕКУЩЕМ состоянии видимости. Требует непустого выделения и активной сессии BEDIT для блока с параметром видимости; иначе — no-op с подсказкой.",
      examples: ["BVSHOW"],
      seeAlso: ["BVHIDE", "BVSHOWALL", "BVSTATE"],
    },
    en: {
      short: "Shows the selected entities in the current visibility state.",
      long: "BVSHOW is the inverse of BVHIDE: it marks the selected entities as visible in the CURRENT visibility state. Requires a non-empty selection and an active BEDIT session for a block with a visibility parameter; otherwise it is a no-op with a hint toast.",
      examples: ["BVSHOW"],
      seeAlso: ["BVHIDE", "BVSHOWALL", "BVSTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bvshow.label",
    labelKeyEn: "command.bvshow.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/bvshow", file: REDUCER_GEOMETRY },
  ],
};

const BVSHOWALL: CommandDescriptor = {
  id: "BVSHOWALL",
  category: "dynamic",
  status: "implemented",
  aiCallable: false,
  aliases: { en: ["BVSHOWALL"], ru: [] },
  commandTypeLiterals: ["geometry/bvshow-all"],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Выбранные сущности внутри block-edit, показываемые во ВСЕХ состояниях видимости.",
        en: "Currently-selected entities inside block-edit to show in EVERY visibility state.",
      },
    },
  ],
  help: {
    ru: {
      short: "Показывает выделенные сущности во всех состояниях видимости.",
      long: "Команда BVSHOWALL отмечает выбранные сущности как видимые во ВСЕХ состояниях видимости параметра (в отличие от BVSHOW, которая затрагивает только текущее состояние). Требует непустого выделения и активной сессии BEDIT для блока с параметром видимости.",
      examples: ["BVSHOWALL"],
      seeAlso: ["BVSHOW", "BVHIDE", "BVSTATE"],
    },
    en: {
      short: "Shows the selected entities in every visibility state.",
      long: "BVSHOWALL marks the selected entities as visible in EVERY state of the visibility parameter (unlike BVSHOW, which only touches the current state). Requires a non-empty selection and an active BEDIT session for a block with a visibility parameter.",
      examples: ["BVSHOWALL"],
      seeAlso: ["BVSHOW", "BVHIDE", "BVSTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bvshowall.label",
    labelKeyEn: "command.bvshowall.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/bvshow-all", file: REDUCER_GEOMETRY },
  ],
};

export const DYNAMIC_AUTHORING_DESCRIPTORS: readonly CommandDescriptor[] = [
  TESTBLOCK,
  CLOSETESTBLOCK,
  BAUTHORPALETTECLOSE,
  BVHIDE,
  BVSHOW,
  BVSHOWALL,
];
